import React, { useMemo, VoidFunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContentHeader } from 'record-it-components/pageContentLayout/PageContentHeader/PageContentHeader';
import { FixedButton } from 'record-it-components/button/FixedButton/FixedButton';
import { PageContentContainer } from 'record-it-components/pageContentLayout/PageContentContainer/PageContentContainer';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import { useNotificationManager } from 'record-it-components/hooks/useNotificationManager';
import {
  MessageBox,
  MessageBoxType
} from 'record-it-components/misc/MessageBox/MessageBox';
import { useStopwatchManager } from '../../../hooks/stopwatch/useStopwatchManager';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { EntityName } from 'record-it-time-common/EntityName';
import { DurationUtils } from 'record-it-utils/utils/DurationUtils/DurationUtils';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import {
  EditWorkTimeEntryBreaksWidget,
  EditWorkTimeEntryBreaksWidgetProps
} from '../../timeTracking/EditWorkTimeEntryBreaksWidget/EditWorkTimeEntryBreaksWidget';
import {
  DateTimeInput,
  DateTimeInputProps
} from 'record-it-components/dateTime/DateTimeInput/DateTimeInput';
import { useAppDispatch } from '../../../store/hooks';
import { createWorkTimeEntrySlice } from '../../../store/slice/createWorkTimeEntry/createWorkTimeEntrySlice';
import { useRequiredWorkTimeEntryToCreate } from '../../../hooks/createWorkTimeEntry/useRequiredWorkTimeEntryToCreate';
import { WorkTimeEntryToCreateActivityWidget } from '../../workTime/WorkTimeEntryToCreateActivityWidget/WorkTimeEntryToCreateActivityWidget';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { WorkTimeEntryUtils } from '../../../classes/entities/WorkTimeEntry/WorkTimeEntryUtils';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { WorkTimeEntryToCreateConstructionSiteWidget } from '../../workTime/WorkTimeEntryToCreateConstructionSiteWidget/WorkTimeEntryToCreateConstructionSiteWidget';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';

import styles from './CreateWorkTimeEntryPage.module.css';
import { useValidation } from '../../../hooks/validation/useValidation';
import { validateWorkTimeEntry } from '../../../hooks/validation/functions/validateWorkTimeEntry';
import {
  TextInput,
  TextInputProps
} from 'record-it-components/input/TextInput/TextInput';

export type CreateWorkTimeEntryPageProps = PropsWithoutChildren<
  Record<string, never>
>;

/**
 * this Page can be put into routes with either the stopwatchId or the userGroupId param
 */
export const CreateWorkTimeEntryPage: VoidFunctionComponent<CreateWorkTimeEntryPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder.addHomePage().addCreateWorkTimeEntryPage()
    );

    const workTimeEntryToCreate = useRequiredWorkTimeEntryToCreate();
    const validationResult = useValidation(
      validateWorkTimeEntry(workTimeEntryToCreate),
      [workTimeEntryToCreate]
    );

    const dispatch = useAppDispatch();

    const { t } = useTranslation();
    const navigate = useNavigate();
    const notificationManager = useNotificationManager();
    const { getRequiredCurrentUser } = useCurrentUser();

    const stopwatchManager = useStopwatchManager();

    const newBreakInitialStartDate = useMemo(
      () => new Date(workTimeEntryToCreate.startDate),
      [workTimeEntryToCreate]
    );
    const newBreakInitialEndDate = useMemo(
      () => new Date(workTimeEntryToCreate.endDate),
      [workTimeEntryToCreate]
    );

    const handleStartDateInput: DateTimeInputProps['onInput'] =
      useEventCallback((newDate) => {
        if (newDate) {
          dispatch(
            createWorkTimeEntrySlice.actions.setStartDate(newDate.toISOString())
          );
        }
      });

    const handleEndDateInput: DateTimeInputProps['onInput'] = useEventCallback(
      (newDate) => {
        if (newDate) {
          dispatch(
            createWorkTimeEntrySlice.actions.setEndDate(newDate.toISOString())
          );
        }
      }
    );

    const handleNoteInput: TextInputProps['onInput'] = useEventCallback(
      (newNote) => {
        dispatch(createWorkTimeEntrySlice.actions.setNote(newNote));
      }
    );

    const handleBreaksModified: EditWorkTimeEntryBreaksWidgetProps['onWorkTimeEntryBreaksModified'] =
      useEventCallback((breaks) => {
        dispatch(createWorkTimeEntrySlice.actions.setBreaks(breaks));
      });

    const workTimeEntryRepository = useEntityRepository(
      EntityName.WorkTimeEntry
    );
    const handleCreateClick = useEventCallback(() => {
      workTimeEntryRepository.create({
        userId: getRequiredCurrentUser().id,
        constructionSiteId: workTimeEntryToCreate.constructionSiteId,
        timeTrackingActivityId: workTimeEntryToCreate.timeTrackingActivityId,
        startDate: workTimeEntryToCreate.startDate,
        endDate: workTimeEntryToCreate.endDate,
        note: workTimeEntryToCreate.note,
        breaks: workTimeEntryToCreate.breaks,
        ownerUserGroupId: workTimeEntryToCreate.userGroupId
      });

      if (workTimeEntryToCreate.stopwatchId != null) {
        stopwatchManager.remove(workTimeEntryToCreate.stopwatchId);
      }

      navigate(RoutePaths.home());
      notificationManager.success({
        title: t(
          'components.page.CreateWorkTimeEntryPage.createdNotification.title'
        ),
        message: t(
          'components.page.CreateWorkTimeEntryPage.createdNotification.message'
        )
      });
    });

    const formattedWorkTime = useMemo(() => {
      return DurationUtils.formatDurationToHourMinuteString(
        WorkTimeEntryUtils.getDuration(
          workTimeEntryToCreate.startDate,
          workTimeEntryToCreate.endDate,
          workTimeEntryToCreate.breaks
        )
      );
    }, [workTimeEntryToCreate]);

    return (
      <PageContentContainer>
        <PageContentHeader
          title={t('components.page.CreateWorkTimeEntryPage.title')}
          iconConfig={
            iconConfigurations.prefixed.page.CreateWorkTimeEntryPage.titleIcon
          }
        ></PageContentHeader>
        {!validationResult.valid ? (
          <MessageBox
            type={MessageBoxType.Error}
            className={styles.ErrorMessageBox}
            data-testid="CreateWorkTimeEntryPage.ErrorMessageBox"
          >
            {validationResult.errors.map((errorText) => {
              return <div key={errorText}>{errorText}</div>;
            })}
          </MessageBox>
        ) : undefined}
        <FormGroup>
          <DateTimeInput
            label={t('components.page.CreateWorkTimeEntryPage.startDate')}
            value={new Date(workTimeEntryToCreate.startDate)}
            onInput={handleStartDateInput}
            data-testid="CreateWorkTimeEntryPage.StartDate"
          ></DateTimeInput>
        </FormGroup>
        <FormGroup>
          <DateTimeInput
            label={t('components.page.CreateWorkTimeEntryPage.endDate')}
            value={new Date(workTimeEntryToCreate.endDate)}
            onInput={handleEndDateInput}
            data-testid="CreateWorkTimeEntryPage.EndDate"
          ></DateTimeInput>
        </FormGroup>
        <FormGroup>
          <WorkTimeEntryToCreateConstructionSiteWidget></WorkTimeEntryToCreateConstructionSiteWidget>
        </FormGroup>
        <FormGroup>
          <WorkTimeEntryToCreateActivityWidget></WorkTimeEntryToCreateActivityWidget>
        </FormGroup>
        <FormGroup>
          <EditWorkTimeEntryBreaksWidget
            workTimeEntryBreaks={workTimeEntryToCreate.breaks}
            onWorkTimeEntryBreaksModified={handleBreaksModified}
            newBreakInitialStartDate={newBreakInitialStartDate}
            newBreakInitialEndDate={newBreakInitialEndDate}
            data-testid="CreateWorkTimeEntryPage.TimeEntryBreaksWidget"
          ></EditWorkTimeEntryBreaksWidget>
        </FormGroup>
        <FormGroup>
          <span>{t('components.page.CreateWorkTimeEntryPage.workTime')}</span>
          <span data-testid="CreateWorkTimeEntryPage.WorkTimeSummaryValue">
            {formattedWorkTime}
          </span>
        </FormGroup>
        <FormGroup>
          <TextInput
            label={t('components.page.CreateWorkTimeEntryPage.note')}
            value={workTimeEntryToCreate.note}
            onInput={handleNoteInput}
          ></TextInput>
        </FormGroup>

        <FixedButton
          onClick={handleCreateClick}
          disabled={!validationResult.valid}
          data-testid="CreateWorkTimeEntryPage.CreateButton"
        >
          {t('components.page.CreateWorkTimeEntryPage.create')}
        </FixedButton>
      </PageContentContainer>
    );
  });
